import { listMaps, voteMap, type MapListItem } from '../api/maps';
import { apiRequest } from '../api';
import { type GameMap, TEAM_FORMATS, type TeamFormat } from '../api/game-refs';
import { MAP_FILTER_TYPES, isLeaguePreset, isMapStyleType, leagueMapTypes, mapTeamFormats, mapTypesWithLeague, teamFormatLabel, type LeagueMapTypes, type MapFilterType } from '../ui/map-presets';
import { state } from '../state';
import { t, onLocaleChange } from '../i18n';
import { navigateTo } from '../router';
import { ensureRolesLoaded } from '../app';
import { initMapModal, openAddMapModal, openEditMapModal } from '../ui/map-modal';
import { buildLinkCopyButton, copyWithToast } from '../ui/copy';
import { attachWhoTooltip, whoNames } from '../ui/admin-who';

// ============================================================
// Публичный раздел «Карты»: список карт с лайками/дизлайками.
// Голосовать можно только авторизованным (иначе редирект на вход).
// ============================================================

let abort: AbortController | null = null;
let items: MapListItem[] = [];
let query = '';
let activeType: MapFilterType | null = null;
let activePlayers: number | null = null;
/** Формат командной карты (2 на 2, 3 на 3, …). */
let activeFormat: TeamFormat | null = null;
let activeAuthor: string | null = null;

let searchInput: HTMLInputElement | null = null;
let filterBar: HTMLElement | null = null;
let listEl: HTMLElement | null = null;
/** id карт по вычисляемым типам из общих списков лиги. */
let leagueTypes: LeagueMapTypes = {
    league: new Set(),
    micro: new Set(),
    macro: new Set(),
    hybrid: new Set(),
};
/** Общие списки (пресеты) вне лиги — фильтр по конкретному списку. */
let listPresets: { name: string; map_ids: number[] }[] = [];
/** Выбранный список (имя общего пресета). */
let activeList: string | null = null;

let previewEl: HTMLElement | null = null;

// --- Права на добавление/правку карт (модератор / ghost / админ, как на сервере) ---
let canEdit = false;

// Живой пересчёт текста при смене языка
onLocaleChange(() => {
    if (!document.getElementById('maps-container')) return;
    if (searchInput) searchInput.placeholder = t('maps.search_placeholder');
    renderFilters();
    renderList();
});

export function mountMaps(): void {
    const screen = document.getElementById('screen-maps');
    if (screen) screen.classList.remove('hidden');

    const container = document.getElementById('maps-container');
    if (!container) return;

    abort = new AbortController();
    const { signal } = abort;

    initMapModal();
    bindAddButton(signal);
    buildLayout(container);

    // Роли подгружаются асинхронно — обновляем права после их получения.
    void ensureRolesLoaded().then(updateEditPermission);
    updateEditPermission();

    void load();
}

export function unmountMaps(): void {
    abort?.abort();
    abort = null;
    hidePreview();
    searchInput = null;
    filterBar = null;
    listEl = null;
}

/** Кнопка «Добавить карту» открывает общую модалку карты. */
function bindAddButton(signal: AbortSignal): void {
    document.getElementById('btn-maps-add')?.addEventListener('click', () => {
        void openAddMapModal().then((saved) => {
            if (saved) upsertMapInList(saved);
        });
    }, { signal });
}

/** Показывает кнопку добавления и делает карточки редактируемыми для модераторов. */
function updateEditPermission(): void {
    canEdit = Boolean(state.user?.is_moderator || state.user?.is_admin || state.user?.is_ghost);
    const addBtn = document.getElementById('btn-maps-add');
    if (addBtn) {
        if (canEdit) addBtn.removeAttribute('hidden');
        else addBtn.setAttribute('hidden', '');
    }
    // Перерисовка списка, чтобы карточки получили/потеряли кликабельность.
    if (listEl && items.length > 0) renderList();
}

async function load(): Promise<void> {
    if (!listEl) return;
    listEl.innerHTML = `<p class="hint">${t('common.loading')}</p>`;
    try {
        // Пресеты нужны, чтобы вычислить типы лиги и собрать фильтр по прочим спискам.
        const [maps, presetRes] = await Promise.all([
            listMaps(state.token),
            apiRequest<{ presets: { name: string; shared: boolean; map_ids: number[] }[] }>(
                '/api/map-presets',
                { token: state.token }
            ).catch(() => ({ presets: [] })),
        ]);
        items = maps;
        const shared = presetRes.presets.filter((p) => p.shared);
        const league = shared.filter(isLeaguePreset);
        // Списки «FFA Лига» в фильтре не показываем — для них есть отдельные фильтры типов.
        listPresets = shared
            .filter((p) => !isLeaguePreset(p))
            .map((p) => ({ name: p.name, map_ids: p.map_ids }));
        leagueTypes = leagueMapTypes(league);
        // Выбранный список мог исчезнуть после перезагрузки — сбрасываем фильтр.
        if (activeList && !listPresets.some((p) => p.name === activeList)) activeList = null;
    } catch (err) {
        console.error('[maps] load error:', err);
        if (listEl) listEl.innerHTML = `<p class="error">${t('maps.error')}</p>`;
        return;
    }
    if (abort?.signal.aborted) return;
    renderFilters();
    renderList();
}

function buildLayout(container: HTMLElement): void {
    container.innerHTML = '';

    const controls = document.createElement('div');
    controls.className = 'maps-controls';

    searchInput = document.createElement('input');
    searchInput.type = 'search';
    searchInput.className = 'maps-search';
    searchInput.placeholder = t('maps.search_placeholder');
    searchInput.value = query;
    searchInput.addEventListener('input', () => {
        query = searchInput!.value.trim().toLowerCase();
        renderList();
    });
    controls.appendChild(searchInput);

    filterBar = document.createElement('div');
    filterBar.className = 'maps-filters';
    controls.appendChild(filterBar);

    container.appendChild(controls);

    listEl = document.createElement('div');
    listEl.className = 'maps-list';
    container.appendChild(listEl);

    renderFilters();
}

function renderFilters(): void {
    if (!filterBar) return;
    filterBar.innerHTML = '';

    // Группа фильтров по типу карты
    const typeGroup = document.createElement('div');
    typeGroup.className = 'maps-filter-group';
    typeGroup.appendChild(makeFilterLabel(t('maps.filter_type')));

    const addTypeBtn = (label: string, type: MapFilterType | null): void => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'maps-filter';
        if (activeType === type) btn.classList.add('is-active');
        btn.textContent = label;
        btn.addEventListener('click', () => {
            activeType = type;
            renderFilters();
            renderList();
        });
        typeGroup.appendChild(btn);
    };

    const types = MAP_FILTER_TYPES.filter((type) =>
        items.some((m) => mapTypesWithLeague(m, leagueTypes).includes(type))
    );
    addTypeBtn(t('maps.filter_all'), null);
    for (const type of types) addTypeBtn(t(`map_type.${type}` as any), type);
    filterBar.appendChild(typeGroup);

    // Фильтр по прочим общим спискам. Пока таких нет — группу не показываем
    // (списки «FFA Лига» покрыты фильтрами типов).
    if (listPresets.length > 0) {
        const listGroup = document.createElement('div');
        listGroup.className = 'maps-filter-group';
        listGroup.appendChild(makeFilterLabel(t('maps.filter_list')));

        const select = document.createElement('select');
        select.className = 'maps-filter-select';
        select.title = t('maps.filter_list');

        const allOption = document.createElement('option');
        allOption.value = '';
        allOption.textContent = t('maps.filter_all_lists');
        select.appendChild(allOption);

        for (const preset of listPresets) {
            const opt = document.createElement('option');
            opt.value = preset.name;
            opt.textContent = `🌐 ${preset.name}`;
            select.appendChild(opt);
        }

        select.value = activeList ?? '';
        select.addEventListener('change', () => {
            activeList = select.value || null;
            renderList();
        });

        listGroup.appendChild(select);
        filterBar.appendChild(listGroup);
    }

    // Группа по числу игроков — только если у карт указано количество.
    const counts = [...new Set(items.map((m) => m.player_count).filter((n): n is number => n != null))]
        .sort((a, b) => a - b);
    if (counts.length > 0) {
        const playersGroup = document.createElement('div');
        playersGroup.className = 'maps-filter-group';
        playersGroup.appendChild(makeFilterLabel(t('maps.filter_players')));

        const addPlayersBtn = (label: string, value: number | null): void => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'maps-filter';
            if (activePlayers === value) btn.classList.add('is-active');
            btn.textContent = label;
            btn.addEventListener('click', () => {
                activePlayers = value;
                renderFilters();
                renderList();
            });
            playersGroup.appendChild(btn);
        };

        addPlayersBtn(t('maps.filter_all'), null);
        for (const c of counts) addPlayersBtn(String(c), c);
        filterBar.appendChild(playersGroup);
    }

    // Группа по формату командных карт — только если форматы размечены.
    const formats = TEAM_FORMATS.filter((f) => items.some((m) => mapTeamFormats(m).includes(f)));
    if (activeFormat != null && !formats.includes(activeFormat)) activeFormat = null;
    if (formats.length > 0) {
        const formatsGroup = document.createElement('div');
        formatsGroup.className = 'maps-filter-group';
        formatsGroup.appendChild(makeFilterLabel(t('maps.filter_formats')));

        const addFormatBtn = (label: string, value: TeamFormat | null): void => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'maps-filter';
            if (activeFormat === value) btn.classList.add('is-active');
            btn.textContent = label;
            btn.addEventListener('click', () => {
                activeFormat = value;
                renderFilters();
                renderList();
            });
            formatsGroup.appendChild(btn);
        };

        addFormatBtn(t('maps.filter_all'), null);
        for (const f of formats) addFormatBtn(teamFormatLabel(f), f);
        filterBar.appendChild(formatsGroup);
    }

    // Группа по автору — выпадающий список, только если авторы указаны.
    const authors = [...new Set(items.map((m) => m.author).filter((a): a is string => !!a))]
        .sort((a, b) => a.localeCompare(b));
    if (activeAuthor != null && !authors.includes(activeAuthor)) activeAuthor = null;
    if (authors.length > 0) {
        const authorGroup = document.createElement('div');
        authorGroup.className = 'maps-filter-group';
        authorGroup.appendChild(makeFilterLabel(t('maps.filter_author')));

        const select = document.createElement('select');
        select.className = 'maps-filter-select';
        select.title = t('maps.filter_author');

        const allOption = document.createElement('option');
        allOption.value = '';
        allOption.textContent = t('maps.filter_all_authors');
        select.appendChild(allOption);

        for (const a of authors) {
            const opt = document.createElement('option');
            opt.value = a;
            opt.textContent = a;
            if (a === activeAuthor) opt.selected = true;
            select.appendChild(opt);
        }

        select.addEventListener('change', () => {
            activeAuthor = select.value || null;
            renderList();
        });

        authorGroup.appendChild(select);
        filterBar.appendChild(authorGroup);
    }
}

/** Подпись группы фильтров. */
function makeFilterLabel(text: string): HTMLElement {
    const span = document.createElement('span');
    span.className = 'maps-filter-label';
    span.textContent = text;
    return span;
}

function matchesQuery(m: MapListItem): boolean {
    if (!query) return true;
    const types = mapTypesWithLeague(m, leagueTypes)
        .map((type) => t(`map_type.${type}` as any))
        .join(' ')
        .toLowerCase();
    const formats = mapTeamFormats(m).map(teamFormatLabel).join(' ').toLowerCase();
    return (
        m.name.toLowerCase().includes(query) ||
        (m.alt_name ?? '').toLowerCase().includes(query) ||
        (m.author ?? '').toLowerCase().includes(query) ||
        types.includes(query) ||
        formats.includes(query)
    );
}

function visibleMaps(): MapListItem[] {
    return items.filter((m) => {
        if (activeType && !mapTypesWithLeague(m, leagueTypes).includes(activeType)) return false;
        if (activeFormat && !mapTeamFormats(m).includes(activeFormat)) return false;
        if (activeList) {
            const ids = new Set(listPresets.find((p) => p.name === activeList)?.map_ids ?? []);
            if (!ids.has(m.id)) return false;
        }
        if (activePlayers != null && m.player_count !== activePlayers) return false;
        if (activeAuthor != null && m.author !== activeAuthor) return false;
        return matchesQuery(m);
    });
}

function renderList(): void {
    if (!listEl) return;
    listEl.innerHTML = '';

    const list = visibleMaps();
    if (list.length === 0) {
        const p = document.createElement('p');
        p.className = 'hint';
        p.textContent = t('maps.empty');
        listEl.appendChild(p);
        return;
    }

    for (const m of list) listEl.appendChild(buildCard(m));
}

function buildCard(m: MapListItem): HTMLElement {
    const card = document.createElement('div');
    card.className = 'map-card' + (m.unbalanced ? ' map-card--unbalanced' : '');
    card.dataset.mapId = String(m.id);

    // --- Картинка ---
    const thumb = document.createElement('div');
    thumb.className = 'map-card-thumb';
    if (m.image_url) {
        const img = document.createElement('img');
        img.src = m.image_url;
        img.alt = '';
        img.loading = 'lazy';
        thumb.appendChild(img);
        thumb.classList.add('is-clickable');
        thumb.title = t('maps.hover_hint');
        thumb.addEventListener('mouseenter', () => showPreview(m.image_url!, thumb));
        thumb.addEventListener('mouseleave', hidePreview);
    } else {
        thumb.classList.add('is-empty');
        thumb.textContent = '🗺';
    }
    card.appendChild(thumb);

    // --- Текст ---
    const body = document.createElement('div');
    body.className = 'map-card-body';

    const name = document.createElement('div');
    name.className = 'map-card-name';
    name.textContent = m.name;
    name.title = t('maps.copy_name_hint');
    name.addEventListener('click', (e) => {
        // Клик по названию копирует его и не открывает редактор карты.
        e.stopPropagation();
        void copyWithToast(m.name);
    });
    body.appendChild(name);

    if (m.alt_name) {
        const alt = document.createElement('div');
        alt.className = 'map-card-alt';
        alt.textContent = m.alt_name;
        body.appendChild(alt);
    }

    if (m.author) {
        const author = document.createElement('div');
        author.className = 'map-card-author';
        author.textContent = t('maps.author', { name: m.author });
        body.appendChild(author);
    }

    if (m.link) {
        // Клик по подписи копирует ссылку и не открывает редактор карты.
        body.appendChild(buildLinkCopyButton(m.link));
    }

    const meta = document.createElement('div');
    meta.className = 'map-card-meta';
    if (m.unbalanced) {
        const b = document.createElement('span');
        b.className = 'map-badge map-badge--unbalanced';
        b.textContent = t('maps.unbalanced_badge');
        b.title = t('maps.unbalanced_hint');
        meta.appendChild(b);
    }
    if (m.player_count != null) {
        const b = document.createElement('span');
        b.className = 'map-badge';
        b.textContent = t('maps.players', { n: m.player_count });
        meta.appendChild(b);
    }
    for (const type of mapTypesWithLeague(m, leagueTypes)) {
        const b = document.createElement('span');
        b.className = 'map-badge map-badge--type' + (isMapStyleType(type) ? ' map-badge--style' : '');
        b.textContent = t(`map_type.${type}` as any);
        meta.appendChild(b);
    }
    // Формат командной карты (2 на 2, 3 на 3, …)
    for (const format of mapTeamFormats(m)) {
        const b = document.createElement('span');
        b.className = 'map-badge map-badge--format';
        b.textContent = teamFormatLabel(format);
        meta.appendChild(b);
    }
    body.appendChild(meta);
    card.appendChild(body);

    // --- Голоса ---
    const votes = document.createElement('div');
    votes.className = 'map-card-votes';

    const likeBtn = document.createElement('button');
    likeBtn.type = 'button';
    likeBtn.className = 'map-vote map-vote--like';
    if (m.my_vote === 1) likeBtn.classList.add('is-active');
    likeBtn.title = t('maps.like');
    likeBtn.innerHTML = `<span class="map-vote-icon">👍</span><span class="map-vote-count">${m.likes}</span>`;
    likeBtn.addEventListener('click', () => void onVote(m, 1));
    attachWhoTooltip(likeBtn, 'map', m.id, (who) =>
      who.votes.up.length > 0 ? `${t('who.likes')}: ${whoNames(who.votes.up)}` : null,
    );

    const dislikeBtn = document.createElement('button');
    dislikeBtn.type = 'button';
    dislikeBtn.className = 'map-vote map-vote--dislike';
    if (m.my_vote === -1) dislikeBtn.classList.add('is-active');
    dislikeBtn.title = t('maps.dislike');
    dislikeBtn.innerHTML = `<span class="map-vote-icon">👎</span><span class="map-vote-count">${m.dislikes}</span>`;
    dislikeBtn.addEventListener('click', () => void onVote(m, -1));
    attachWhoTooltip(dislikeBtn, 'map', m.id, (who) =>
      who.votes.down.length > 0 ? `${t('who.dislikes')}: ${whoNames(who.votes.down)}` : null,
    );

    votes.append(likeBtn, dislikeBtn);
    card.appendChild(votes);

    // Клик по карточке открывает правку карты (для модератора/ghost/админа).
    if (canEdit) {
        card.classList.add('is-editable');
        card.title = t('maps.edit_hint');
        card.addEventListener('click', (e) => {
            // Кнопки голосования не должны открывать редактор.
            if ((e.target as HTMLElement).closest('.map-card-votes')) return;
            void openEditMapModal(m).then((saved) => {
                if (saved) upsertMapInList(saved);
            });
        });
    }

    return card;
}

async function onVote(m: MapListItem, value: 1 | -1): Promise<void> {
    if (!state.user) {
        // Голосовать могут только авторизованные
        navigateTo('/auth');
        return;
    }
    const next = (m.my_vote === value ? 0 : value) as 1 | -1 | 0;
    try {
        const r = await voteMap(m.id, next, state.token);
        m.likes = r.likes;
        m.dislikes = r.dislikes;
        m.my_vote = r.my_vote;
        renderList();
    } catch (err) {
        console.error('[maps] vote error:', err);
    }
}

/** Преобразует карту из API справочника в элемент публичного списка. */
function apiMapToListItem(g: GameMap): MapListItem {
    const prev = items.find((m) => m.id === g.id);
    return {
        id: g.id,
        name: g.name,
        alt_name: g.alt_name ?? null,
        map_types: g.map_types ?? [],
        team_formats: g.team_formats ?? [],
        player_count: g.player_count ?? null,
        author: g.author ?? null,
        link: g.link ?? null,
        unbalanced: Boolean(g.unbalanced),
        image_url: g.image_url ?? null,
        likes: prev?.likes ?? 0,
        dislikes: prev?.dislikes ?? 0,
        my_vote: prev?.my_vote ?? 0,
    };
}

/** Вставляет карточку в правильную позицию видимого списка (без перерисовки). */
function insertVisibleCard(item: MapListItem): void {
    if (!listEl) return;
    const orderIds = visibleMaps().map((m) => m.id);
    const pos = orderIds.indexOf(item.id);
    const nodes = [...listEl.querySelectorAll<HTMLElement>('.map-card')];
    let before: Node | null = null;
    for (const n of nodes) {
        const np = orderIds.indexOf(Number(n.dataset.mapId));
        if (np > pos) { before = n; break; }
    }
    listEl.insertBefore(buildCard(item), before);
    listEl.querySelector('.hint')?.remove();
}

/**
 * Добавляет/обновляет карту в списке без перезагрузки страницы: меняем только
 * её карточку (или вставляем/удаляем), поиск и фильтры не сбрасываются.
 */
function upsertMapInList(g: GameMap): void {
    if (!listEl) return;
    const item = apiMapToListItem(g);
    const idx = items.findIndex((m) => m.id === item.id);
    if (idx === -1) items.push(item);
    else items[idx] = item;
    items.sort((a, b) => a.name.localeCompare(b.name));

    // Набор типов мог измениться — обновляем кнопки фильтра.
    renderFilters();

    const isVisible = visibleMaps().some((m) => m.id === item.id);
    const existing = listEl.querySelector<HTMLElement>(`.map-card[data-map-id="${item.id}"]`);

    if (!isVisible) {
        existing?.remove();
        if (!listEl.querySelector('.map-card')) {
            listEl.innerHTML = `<p class="hint">${t('maps.empty')}</p>`;
        }
        return;
    }

    // Переставляем карточку заново, чтобы учесть возможную смену имени/порядка.
    existing?.remove();
    insertVisibleCard(item);
}

// --- Копирование в буфер + всплывашка — см. ../ui/copy.ts ---

// --- Крупный предпросмотр картинки при наведении ---
function hidePreview(): void {
    previewEl?.remove();
    previewEl = null;
}

function showPreview(src: string, anchor: HTMLElement): void {
    hidePreview();
    const box = document.createElement('div');
    box.className = 'map-preview';
    const img = document.createElement('img');
    img.src = src;
    img.alt = '';
    box.appendChild(img);
    document.body.appendChild(box);

    const r = anchor.getBoundingClientRect();
    const b = box.getBoundingClientRect();
    const pad = 8;

    // Справа от строки, но не за краем экрана
    let left = r.right + 14;
    if (left + b.width > window.innerWidth - pad) left = window.innerWidth - b.width - pad;
    if (left < pad) left = pad;

    // По вертикали — прижимаем так, чтобы превью целиком влезало в окно
    let top = r.top;
    if (top + b.height > window.innerHeight - pad) top = window.innerHeight - b.height - pad;
    if (top < pad) top = pad;

    box.style.left = `${left}px`;
    box.style.top = `${top}px`;
    previewEl = box;
}


